import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { ProvinceStateCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { CountryCreate } from "../../../country/components/create/CountryCreate";
import { CountryCreateStore } from "../../../country/store";
import { FormControl } from "@mui/material";

export type ProvinceStateCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name_esSelectedValue?: { displayId: string; id: string } | null;
  onName_esChange?: (value: string) => void;
  name_es: string;
  name_esError?: string;
  name_enSelectedValue?: { displayId: string; id: string } | null;
  onName_enChange?: (value: string) => void;
  name_en: string;
  name_enError?: string;
  name_ptSelectedValue?: { displayId: string; id: string } | null;
  onName_ptChange?: (value: string) => void;
  name_pt: string;
  name_ptError?: string;
  countrySelectedValue?: { displayId: string; id: string } | null;
  onCountrySelected?: (value: { displayId: string; id: string } | null) => void;
  onCountryChange?: (value: string) => void;
  countryOptions?: { displayId: string; id: string }[];
  countryError?: string;
  provinceStateCreateStore: ProvinceStateCreateStore;
};

export const ProvinceStateCreateForm: React.FC<ProvinceStateCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name_es = "",
      name_esError,
      onName_esChange = () => {},
      name_en = "",
      name_enError,
      onName_enChange = () => {},
      name_pt = "",
      name_ptError,
      onName_ptChange = () => {},
      onCountrySelected = () => {},
      countrySelectedValue = null,
      countryOptions = [],
      countryError,
      onCountryChange = () => {},
      provinceStateCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="ProvinceState"
              mode="create"
              values={{}}
              options={{ country: countryOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name_es":
                    onName_esChange?.(valor);
                    break;
                  case "name_en":
                    onName_enChange?.(valor);
                    break;
                  case "name_pt":
                    onName_ptChange?.(valor);
                    break;
                  case "country":
                    onCountrySelected?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={provinceStateCreateStore.getCardErrors("null")}
              title={langStore.groupProvinceStateLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name_es}
                  error={Boolean(name_esError)}
                  warning={
                    !name_esError &&
                    Boolean(provinceStateCreateStore.warnings["name_es"])
                  }
                  helperText={
                    name_esError ||
                    provinceStateCreateStore.warnings["name_es"]?.message
                  }
                  label={langStore.ProvinceStateNameLabel + " (es)"}
                  onChange={(value: string) => onName_esChange(value)}
                  description={langStore.ProvinceStateNameDescription}
                />

                <InputString
                  value={name_en}
                  error={Boolean(name_enError)}
                  warning={
                    !name_enError &&
                    Boolean(provinceStateCreateStore.warnings["name_en"])
                  }
                  helperText={
                    name_enError ||
                    provinceStateCreateStore.warnings["name_en"]?.message
                  }
                  label={langStore.ProvinceStateNameLabel + " (en)"}
                  onChange={(value: string) => onName_enChange(value)}
                  description={langStore.ProvinceStateNameDescription}
                />

                <InputString
                  value={name_pt}
                  error={Boolean(name_ptError)}
                  warning={
                    !name_ptError &&
                    Boolean(provinceStateCreateStore.warnings["name_pt"])
                  }
                  helperText={
                    name_ptError ||
                    provinceStateCreateStore.warnings["name_pt"]?.message
                  }
                  label={langStore.ProvinceStateNameLabel + " (pt)"}
                  onChange={(value: string) => onName_ptChange(value)}
                  description={langStore.ProvinceStateNameDescription}
                />
                <InputReference
                  id="country"
                  label={langStore.ProvinceStateCountryLabel}
                  error={Boolean(countryError)}
                  helperText={
                    countryError ||
                    provinceStateCreateStore.warnings["country"]?.message
                  }
                  selectedValue={countrySelectedValue}
                  onChange={(value: string) => onCountryChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onCountrySelected(value)
                  }
                  options={countryOptions}
                  description={langStore.ProvinceStateCountryDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "country_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new CountryCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <CountryCreate
                          countryCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewCountry"
                      cancelMethod="cancelNewCountry"
                    />
                  )}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
